import React, { useState } from 'react';
import {
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Box,
  Icon,
  useColorModeValue,
  Badge,
  Button,
  Progress,
  Grid,
  Flex,
  CircularProgress,
  CircularProgressLabel,
  useToast,
} from '@chakra-ui/react';
import { FiCheck, FiFileText, FiUser, FiSettings, FiShield, FiPenTool, FiHome, FiTruck, FiUsers } from 'react-icons/fi';
import { vendorApi, VendorRegistrationData } from 'api/vendor-api';

// Step components
import CompanyInformation from './steps/CompanyInformation';
import VendorTypeSelection from './steps/VendorTypeSelection';
import ContactInformation from './steps/ContactInformation';
import PaymentInformation from './steps/BankingPayment';
import FreightShipping from './steps/FreightShipping';
import DiversityClassifications from './steps/DiversityClassifications';
import DocumentUpload from './steps/DocumentUpload';
import BestInClassSignatureStep from './steps/BestInClassSignatureStep';
import RegistrationComplete from './steps/RegistrationComplete';

export interface RegistrationData {
  vendorType: 'manufacturer' | 'installation' | '';
  companyInfo: {
    name: string;
    legalName?: string;
    businessType?: string;
    vendorLocation?: string;
    productService?: string;
    phone: string;
    fax?: string;
    mainAddress?: string;
    suite?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    remitAddress?: string;
    remitCountry?: string;
    shipAddress?: string;
    shipCountry?: string;
    email: string;
    website: string;
    contactPerson: string;
    businessLicense: string;
    taxId: string;
    ein?: string;
  };
  contactInfo?: {
    poEmail?: string;
    primaryContact?: {
      firstName?: string;
      lastName?: string;
      email?: string;
    };
    customerSupport?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    };
    accounting?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    };
  };
  paymentInfo?: {
    paymentTerms?: string;
    customPaymentTerms?: string;
    pricingApproach?: string;
    pricingDetails?: string;
    depositRequired?: boolean;
    depositDetails?: string;
    acknowledgements?: boolean;
    acknowledgementProcess?: string;
  };
  banking?: {
    accountName?: string;
    accountNumber?: string;
    routingNumber?: string;
    bankName?: string;
    taxDocuments?: File[];
  };
  freightShipping?: {
    freightPolicy?: string;
    fobPoint?: string;
    standardLeadTime?: string;
    estimatedTransitDays?: string;
  };
  diversityClassifications?: {
    designations?: string[];
    unicorSuppliers?: boolean;
    abilityOneSuppliers?: boolean;
    hubZone?: boolean;
  };
  terms: {
    acknowledged: boolean;
    acknowledgedDate: Date | null;
    version: string;
  };
  achAuthorization: {
    signed: boolean;
    signedDate: Date | null;
    envelopeId: string | null;
  };
  depositAgreement: {
    signed: boolean;
    signedDate: Date | null;
    envelopeId: string | null;
  };
  w9Form: {
    signed: boolean;
    signedDate: Date | null;
    envelopeId: string | null;
    taxClassification: string;
  };
  uploadedDocuments?: any[];
  contract: {
    signed: boolean;
    signedDate: Date | null;
    signatureData: string;
  };
  vendorAgreement?: {
    signed: boolean;
    signedDate: Date | null;
    envelopeId: string | null;
  };
  signatures?: {
    w9?: { completed: boolean; signatureRequestId?: string };
    ach?: { completed: boolean; signatureRequestId?: string };
    terms?: { completed: boolean; signatureRequestId?: string };
  };
  signaturesCompleted?: boolean;
}

const VendorRegistration: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentDocumentStep, setCurrentDocumentStep] = useState(0);
  const toast = useToast();


  const [registrationData, setRegistrationData] = useState<RegistrationData>({
    vendorType: '',
    companyInfo: {
      name: '',
      legalName: '',
      businessType: '',
      vendorLocation: '',
      productService: '',
      phone: '',
      fax: '',
      mainAddress: '',
      suite: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      remitAddress: '',
      shipAddress: '',
      email: '',
      website: '',
      contactPerson: '',
      businessLicense: '',
      taxId: '',
    },
    contactInfo: {
      poEmail: '',
      customerSupport: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      accounting: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
    },
    paymentInfo: {
      paymentTerms: '',
      customPaymentTerms: '',
      pricingApproach: '',
      pricingDetails: '',
      depositRequired: false,
      depositDetails: '',
      acknowledgements: false,
      acknowledgementProcess: '',
    },
    banking: {
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      bankName: '',
      taxDocuments: [],
    },
    freightShipping: {
      freightPolicy: '',
      fobPoint: '',
      standardLeadTime: '',
      estimatedTransitDays: '',
    },
    diversityClassifications: {
      designations: [],
      unicorSuppliers: false,
      abilityOneSuppliers: false,
      hubZone: false,
    },
    terms: {
      acknowledged: false,
      acknowledgedDate: null,
      version: '1.0',
    },
    contract: {
      signed: false,
      signedDate: null,
      signatureData: '',
    },
    achAuthorization: {
      signed: false,
      signedDate: null,
      envelopeId: null,
    },
    depositAgreement: {
      signed: false,
      signedDate: null,
      envelopeId: null,
    },
    w9Form: {
      signed: false,
      signedDate: null,
      envelopeId: null,
      taxClassification: '',
    },
    uploadedDocuments: [],
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const steps = [
    {
      id: 0,
      title: 'Vendor Type Selection',
      description: 'Choose your vendor category',
      icon: FiHome,
      component: VendorTypeSelection,
      required: true,
    },
    {
      id: 1,
      title: 'Company Information',
      description: 'Basic company details and verification',
      icon: FiUser,
      component: CompanyInformation,
      required: true,
    },
    {
      id: 2,
      title: 'Contact Information',
      description: 'Contact details for different departments',
      icon: FiShield,
      component: ContactInformation,
      required: true,
    },
    {
      id: 3,
      title: 'Payment Information',
      description: 'Payment terms, pricing, and acknowledgements',
      icon: FiSettings,
      component: PaymentInformation,
      required: true,
    },
    {
      id: 4,
      title: 'Freight and Shipping',
      description: 'Freight policy, FOB point, and shipping timing',
      icon: FiTruck,
      component: FreightShipping,
      required: true,
    },
    {
      id: 5,
      title: 'Diversity Classifications',
      description: 'Diversity certifications and supplier programs',
      icon: FiUsers,
      component: DiversityClassifications,
      required: false,
    },
    {
      id: 6,
      title: 'Electronic Signatures',
      description: 'Complete all required document signatures (W-9, ACH, Terms)',
      icon: FiPenTool,
      component: BestInClassSignatureStep,
      required: true,
    },
    {
      id: 7,
      title: 'Document Upload',
      description: 'Upload additional business documents (license, certifications, etc.)',
      icon: FiFileText,
      component: DocumentUpload,
      required: false,
    },
    {
      id: 8,
      title: 'Registration Complete',
      description: 'Account activation and next steps',
      icon: FiCheck,
      component: RegistrationComplete,
      required: false,
    },
  ];

  const getStepStatus = (stepId: number) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'pending';
  };

  const completedSteps = currentStep;
  const totalSteps = steps.length - 1; // Exclude completion step
  const progressPercentage = (completedSteps / totalSteps) * 100;

  // Validation function for each step
  const isCurrentStepValid = (): boolean => {
    switch (currentStep) {
      case 0: // Vendor Type Selection
        return registrationData.vendorType === 'manufacturer' || registrationData.vendorType === 'installation';
      case 1: // Company Information
        return !!(registrationData.companyInfo.name && registrationData.companyInfo.phone && registrationData.companyInfo.email);
      // Add more validations for other steps as needed
      default:
        return true;
    }
  };

  const nextStep = () => {
    // Validate current step before proceeding
    if (!isCurrentStepValid()) {
      if (currentStep === 0) {
        toast({
          title: 'Vendor Type Required',
          description: 'Please select your vendor type before proceeding.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
      return;
    }

    // If this is the last step, submit the registration
    if (currentStep === steps.length - 2) {
      submitRegistration();
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentStepComponent = steps[currentStep].component;

  // Helper function to convert registration data to API format
  const convertToApiFormat = (data: RegistrationData): VendorRegistrationData => {
    return {
      companyName: data.companyInfo.name,
      vendorName: data.companyInfo.name,
      legalBusinessName: data.companyInfo.legalName || data.companyInfo.name,
      email: data.companyInfo.email,
      phone: data.companyInfo.phone,
      businessType: data.companyInfo.businessType,
      federalTaxId: data.companyInfo.taxId,
      website: data.companyInfo.website,
      businessAddress: {
        street: data.companyInfo.mainAddress || '',
        city: data.companyInfo.city || '',
        state: data.companyInfo.state || '',
        zipCode: data.companyInfo.postalCode || '',
        country: data.companyInfo.country || 'United States'
      },
      primaryContactName: data.companyInfo.contactPerson,
      primaryContactEmail: data.companyInfo.email,
      primaryContactPhone: data.companyInfo.phone,
      businessStructure: data.companyInfo.businessType,
      notes: `Vendor Type: ${data.vendorType}. Payment Terms: ${data.paymentInfo?.paymentTerms || 'Not specified'}.`,
      status: 'Pending'
    };
  };

  // Function to submit registration to live backend
  const submitRegistration = async () => {
    try {
      setIsSubmitting(true);
      
      // Convert registration data to API format
      const apiData = convertToApiFormat(registrationData);
      
      console.log('🔄 Submitting vendor registration to live backend...');
      
      // Submit to live backend
      const result = await vendorApi.registerVendor(apiData);
      
      if (result.success) {
        console.log('✅ Vendor registration successful:', result);
        
        // Show success message
        toast({
          title: 'Registration Successful!',
          description: `Vendor registered successfully. ${result.pdf?.generated ? 'PDF generated.' : ''} ${result.sharepoint?.configured ? 'SharePoint sync pending.' : ''}`,
          status: 'success',
          duration: 8000,
          isClosable: true,
        });
        
        // Store vendor ID for future reference
        if (result.vendorId) {
          localStorage.setItem('vendorId', result.vendorId);
        }
        
        // Move to completion step or redirect
        setCurrentStep(steps.length); // Move beyond the last step to show completion
        
      } else {
        throw new Error(result.error || 'Registration failed');
      }
      
    } catch (error) {
      console.error('❌ Registration failed:', error);
      toast({
        title: 'Registration Failed',
        description: error instanceof Error ? error.message : 'An error occurred during registration. Please try again.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box w="full" p={6}>
      {/* Progress Header */}
      <Card mb={8} bg={bgColor} borderColor={borderColor}>
        <CardBody>
          <VStack spacing={6}>
            <VStack align="center" spacing={2}>
              <CircularProgress 
                value={progressPercentage} 
                size="80px" 
                color="blue.400"
                thickness={8}
              >
                <CircularProgressLabel>
                  <Text fontSize="sm" fontWeight="bold">
                    {Math.round(progressPercentage)}%
                  </Text>
                </CircularProgressLabel>
              </CircularProgress>
              <Text fontSize="sm" color="gray.600">
                {completedSteps} of {totalSteps} steps
              </Text>
            </VStack>

            <Progress 
              value={progressPercentage} 
              w="full" 
              h={2} 
              colorScheme="blue" 
              borderRadius="full"
            />
          </VStack>
        </CardBody>
      </Card>

      <Grid 
        templateColumns={{ base: '1fr', xl: '1fr 2fr' }} 
        gap={8}
      >
        {/* Step Checklist */}
        <VStack spacing={4} align="stretch">
          <Text fontSize="xl" fontWeight="bold" mb={2}>
            Registration Checklist
          </Text>
          
          {steps.map((step) => {
            const status = getStepStatus(step.id);
            
            return (
              <Card 
                key={step.id}
                bg={status === 'current' ? 'blue.50' : bgColor}
                borderColor={status === 'current' ? 'blue.200' : borderColor}
                borderWidth={status === 'current' ? 2 : 1}
                cursor={step.id < currentStep ? 'pointer' : 'default'}
                onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                _hover={step.id < currentStep ? { bg: 'gray.50' } : {}}
              >
                <CardBody>
                  <HStack spacing={4}>
                    <Flex
                      w={10}
                      h={10}
                      bg={
                        status === 'completed' 
                          ? 'green.500' 
                          : status === 'current' 
                          ? 'blue.500' 
                          : 'gray.300'
                      }
                      borderRadius="full"
                      align="center"
                      justify="center"
                    >
                      <Icon
                        as={status === 'completed' ? FiCheck : step.icon}
                        color="white"
                        fontSize="sm"
                      />
                    </Flex>
                    
                    <VStack align="start" spacing={1} flex={1}>
                      <HStack>
                        <Text fontWeight="semibold" fontSize="sm">
                          {step.title}
                        </Text>
                        <Badge
                          colorScheme={
                            status === 'completed' 
                              ? 'green' 
                              : status === 'current' 
                              ? 'blue' 
                              : 'gray'
                          }
                          size="sm"
                        >
                          {status === 'completed' 
                            ? 'Complete' 
                            : status === 'current' 
                            ? 'In Progress' 
                            : 'Pending'
                          }
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.600">
                        {step.description}
                      </Text>
                    </VStack>
                  </HStack>
                </CardBody>
              </Card>
            );
          })}
        </VStack>

        {/* Current Step Content */}
        <Box>
          <Card bg={bgColor} borderColor={borderColor}>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <VStack align="start" spacing={2}>
                  <HStack>
                    <Icon as={steps[currentStep].icon} color="blue.500" />
                    <Text fontSize="xl" fontWeight="bold">
                      {steps[currentStep].title}
                    </Text>
                  </HStack>
                  <Text color="gray.600">
                    {steps[currentStep].description}
                  </Text>
                </VStack>

                {/* Step Component */}
                <Box minH="400px">
                  <CurrentStepComponent
                    data={registrationData}
                    updateData={(partialData: Partial<RegistrationData>) => 
                      setRegistrationData(prevData => ({ ...prevData, ...partialData }))
                    }
                    onNext={nextStep}
                    onPrevious={previousStep}
                    {...(currentStep === 4 && {
                      currentDocumentStep,
                      setCurrentDocumentStep
                    })}
                  />
                </Box>

                {/* Navigation Buttons */}
                {currentStep < steps.length - 1 && (
                  <HStack justify="space-between">
                    <Button
                      variant="outline"
                      onClick={previousStep}
                      isDisabled={currentStep === 0}
                    >
                      Previous
                    </Button>
                    
                    <Button
                      colorScheme="blue"
                      onClick={nextStep}
                      isLoading={isSubmitting}
                      isDisabled={!isCurrentStepValid()}
                    >
                      {currentStep === steps.length - 2 ? 'Complete Registration' : 'Next Step'}
                    </Button>
                  </HStack>
                )}
              </VStack>
            </CardBody>
          </Card>
        </Box>
      </Grid>
    </Box>
  );
};

export default VendorRegistration; 